import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Package, Calendar, TrendUp, Trash, Pencil, Database, Plus, X, ListChecks, Wallet, CheckCircle, XCircle, HourglassSimple, MagnifyingGlass, UserCircleGear } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import IdBadge from '../components/IdBadge';
import AdminKycPanel from '../components/AdminKycPanel';
import AdminJoiningFeePanel from '../components/AdminJoiningFeePanel';
import AdminLeadPacksPanel from '../components/AdminLeadPacksPanel';
import AdminProfessionalsPanel from '../components/AdminProfessionalsPanel';
import AdminServiceCitiesCard from '../components/AdminServiceCitiesCard';
import AdminCityDemandHeatmap from '../components/AdminCityDemandHeatmap';
import AdminMediaCapsCard from '../components/AdminMediaCapsCard';
import AdminModerationToggleCard from '../components/AdminModerationToggleCard';
import AdminIntegrationKeysCard from '../components/AdminIntegrationKeysCard';
import AdminInviteEarnToggleCard from '../components/AdminInviteEarnToggleCard';
import AdminTemplatesCard from '../components/AdminTemplatesCard';
import AdminImageNamingCard from '../components/AdminImageNamingCard';
import AdminSEOCitiesCard from '../components/AdminSEOCitiesCard';
import AdminSEOAdvancedCard from '../components/AdminSEOAdvancedCard';
import AdminAISearchCard from '../components/AdminAISearchCard';
import AdminRazorpayCard from '../components/AdminRazorpayCard';
import AdminSegmentImagesCard from '../components/AdminSegmentImagesCard';
import AdminMarketingInviteCard from '../components/AdminMarketingInviteCard';
import AdminAutoBlastCard from '../components/AdminAutoBlastCard';
import AdminAppApkCard from '../components/AdminAppApkCard';
import AdminContactFinderCard from '../components/AdminContactFinderCard';
import AdminGoogleSearchCard from '../components/AdminGoogleSearchCard';
import AdminMagicLinkCard from '../components/AdminMagicLinkCard';
import AdminSegmentRulesCard from '../components/AdminSegmentRulesCard';
import AdminCalendarRulesCard from '../components/AdminCalendarRulesCard';
import AdminKycFormBuilderCard from '../components/AdminKycFormBuilderCard';
import AdminEventCityHeatmap from '../components/AdminEventCityHeatmap';
import AdminProResponseWindow from '../components/AdminProResponseWindow';
import AdminVendorPaymentModePanel from '../components/AdminVendorPaymentModePanel';
import { isNativeApp } from '../utils/platform';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorEditData, setVendorEditData] = useState({});
  const [vendorFilter, setVendorFilter] = useState('all'); // 'all' | 'empty'
  const [vendorImpersonatingId, setVendorImpersonatingId] = useState(null);
  const [quickAddItems, setQuickAddItems] = useState([]);
  const [quickAddCategory, setQuickAddCategory] = useState('caterer');
  const [newItem, setNewItem] = useState({ name: '', defaultPrice: '', category: '', item_type: '' });
  const [withdrawals, setWithdrawals] = useState([]);
  const [referralSettings, setReferralSettings] = useState({ commission_percentage: 5, required_invites: 10, period_days: 30 });
  const [settingsEditing, setSettingsEditing] = useState(false);
  const [packageBookings, setPackageBookings] = useState([]);
  const [expandedPkgBooking, setExpandedPkgBooking] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [newCaptain, setNewCaptain] = useState({ phone: '', name: '', location: '' });
  const [geofenceRadius, setGeofenceRadius] = useState(200);
  const [waiterTimeoutMin, setWaiterTimeoutMin] = useState(30);
  const [vendorPolicy, setVendorPolicy] = useState({ vendor_offer_timeout_min: 1440, vendor_max_misses: 3 });
  const [proFees, setProFees] = useState({
    pro_confirmation_fee_inr: 1000,
    waiter_confirmation_fee_inr: 200,
    refund_window_days: 3,
    refund_early_pct: 50,
    refund_late_pct: 25,
  });
  const [appVersionForm, setAppVersionForm] = useState({
    min_version: '0.0.0',
    latest_version: '',
    play_store_url: '',
    update_message: '',
  });
  // Calendar busy-day lift rules (admin-controlled max free lifts + paid lift fee).
  const [calendarRules, setCalendarRules] = useState({ max_free_lifts: 3, pay_amount_inr: 100 });
  const [segmentsConfig, setSegmentsConfig] = useState(null); // { segments: { [eventType]: { [segKey]: bool } }, segment_keys, event_types }
  const [savingSegments, setSavingSegments] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);
  const [reports, setReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const SEGMENT_LABELS = {
    venue: 'Venue', caterer: 'Caterer', photographer: 'Photographer',
    dj: 'DJ', flowers: 'Flowers', equipment: 'Equipment',
    professional: 'Professional (Pundit/Mehendi/Makeup)', birthday_deco: 'Birthday Decoration',
    custom_decor_packages: 'Simple Wedding Decoration',
    indoor_decor: 'Indoor Decor', outdoor_decor: 'Outdoor Decor', lighting_decor: 'Lighting Decor',
    flower_decor: 'Flower Decor', house_decoration: 'House Decoration',
    nadaswaram_thavil: 'Nadaswaram & Thavil',
    birthday_artists: 'Buffoon / Mimicry / Singer',
  };
  const EVENT_LABELS = {
    marriage: 'Marriage / Wedding', birthday: 'Birthday', engagement: 'Engagement',
    baby_shower: 'Baby Shower', house_warming: 'House Warming', corporate: 'Corporate',
    anniversary: 'Anniversary', puberty_ceremony: 'Puberty Ceremony', retirement: 'Retirement',
    religious: 'Religious', other: 'Other',
  };

  const toggleSegment = (eventType, segKey) => {
    setSegmentsConfig(prev => ({
      ...prev,
      segments: {
        ...prev.segments,
        [eventType]: { ...prev.segments[eventType], [segKey]: !prev.segments[eventType]?.[segKey] },
      },
    }));
  };

  const saveSegments = async () => {
    if (!segmentsConfig) return;
    setSavingSegments(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/instant-pricing-segments`,
        { segments: segmentsConfig.segments }, { withCredentials: true });
      toast.success('Pricing segments saved');
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Save failed');
    } finally {
      setSavingSegments(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, vendorsRes, customersRes, servicesRes, bookingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/vendors`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/customers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/all-services`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/all-bookings`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
      // Fetch withdrawals and referral settings separately (non-blocking)
      try {
        const [wRes, sRes, pbRes, capRes, appRes, segRes, drRes, ssRes, repRes, calRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/withdrawals`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/referral/settings`),
          axios.get(`${BACKEND_URL}/api/admin/package-bookings`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/captains`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/app-settings`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/instant-pricing-segments`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/deletion-requests`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/site-settings`),
          axios.get(`${BACKEND_URL}/api/admin/reports`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/calendar-rules`, { withCredentials: true }),
        ]);
        setWithdrawals(wRes.data);
        setReferralSettings(sRes.data);
        setPackageBookings(pbRes.data);
        setCaptains(capRes.data);
        setGeofenceRadius(appRes.data?.geofence_radius_m || 200);
        setWaiterTimeoutMin(appRes.data?.waiter_offer_timeout_min || 30);
        setVendorPolicy({
          vendor_offer_timeout_min: appRes.data?.vendor_offer_timeout_min || 1440,
          vendor_max_misses: appRes.data?.vendor_max_misses || 3,
        });
        setProFees({
          pro_confirmation_fee_inr: appRes.data?.pro_confirmation_fee_inr || 1000,
          waiter_confirmation_fee_inr: appRes.data?.waiter_confirmation_fee_inr || 200,
          refund_window_days: appRes.data?.refund_window_days ?? 3,
          refund_early_pct: appRes.data?.refund_early_pct ?? 50,
          refund_late_pct: appRes.data?.refund_late_pct ?? 25,
        });
        setAppVersionForm({
          min_version: appRes.data?.min_app_version || '0.0.0',
          latest_version: appRes.data?.latest_app_version || '',
          play_store_url: appRes.data?.play_store_url || '',
          update_message: appRes.data?.update_message || '',
        });
        setSegmentsConfig(segRes.data);
        setDeletionRequests(drRes.data || []);
        setSiteSettings(ssRes.data);
        setReports(repRes.data || []);
        if (calRes?.data) setCalendarRules({
          max_free_lifts: Number(calRes.data.max_free_lifts) || 3,
          pay_amount_inr: Number(calRes.data.pay_amount_inr) || 100,
        });
      } catch {}
    } catch (error) {
      toast.error('Error loading admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor.id);
    setVendorEditData({
      name: vendor.name,
      business_name: vendor.business_name || '',
      location: vendor.location || '',
      description: vendor.description || '',
    });
  };

  const handleApproveWithdrawal = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/withdrawals/${userId}/approve`, {}, { withCredentials: true });
      toast.success('Withdrawal approved!');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleRejectWithdrawal = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/withdrawals/${userId}/reject`, {}, { withCredentials: true });
      toast.success('Withdrawal rejected, amount refunded');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveReferralSettings = async () => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/referral-settings`, referralSettings, { withCredentials: true });
      setReferralSettings(data);
      setSettingsEditing(false);
      toast.success('Referral settings updated!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update settings'); }
  };

  const handleUpdatePackageBookingStatus = async (bookingId, status) => {
    try {
      const { data } = await axios.patch(`${BACKEND_URL}/api/admin/package-bookings/${bookingId}/status`, { status }, { withCredentials: true });
      const captain = data?.captain_assigned;
      toast.success(captain ? `Status updated → captain ${captain.captain_name} auto-assigned` : `Status updated to ${status.replace('_', ' ')}`);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const handleCreateCaptain = async () => {
    if (!newCaptain.phone || !newCaptain.name) { toast.error('Phone and name required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/captains`, newCaptain, { withCredentials: true });
      toast.success('Captain created');
      setNewCaptain({ phone: '', name: '', location: '' });
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create captain'); }
  };

  const handleUpdateCaptain = async (id, patch) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/captains/${id}`, patch, { withCredentials: true });
      toast.success('Updated');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCaptain = async (id) => {
    if (!window.confirm('Delete this captain?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/captains/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveGeofence = async () => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/app-settings/geofence`, { geofence_radius_m: parseInt(geofenceRadius, 10) }, { withCredentials: true });
      setGeofenceRadius(data.geofence_radius_m);
      toast.success(`Geofence radius set to ${data.geofence_radius_m}m`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveWaiterTimeout = async () => {
    const v = parseInt(waiterTimeoutMin, 10);
    if (Number.isNaN(v) || v < 1 || v > 1440) {
      toast.error('Timeout must be 1–1440 minutes');
      return;
    }
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/app-settings/waiter-timeout`, { waiter_offer_timeout_min: v }, { withCredentials: true });
      setWaiterTimeoutMin(data.waiter_offer_timeout_min);
      toast.success(`Waiter offer timeout set to ${data.waiter_offer_timeout_min} min`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveCalendarRules = async () => {
    const lifts = parseInt(calendarRules.max_free_lifts, 10);
    const amount = parseInt(calendarRules.pay_amount_inr, 10);
    if (Number.isNaN(lifts) || lifts < 0 || lifts > 1000) { toast.error('Free lifts must be 0–1000'); return; }
    if (Number.isNaN(amount) || amount < 1 || amount > 100000) { toast.error('Pay amount must be ₹1–₹100000'); return; }
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/calendar-rules`,
        { max_free_lifts: lifts, pay_amount_inr: amount }, { withCredentials: true });
      setCalendarRules({
        max_free_lifts: Number(data.max_free_lifts) || lifts,
        pay_amount_inr: Number(data.pay_amount_inr) || amount,
      });
      toast.success(`Calendar rules updated · ${data.max_free_lifts} free lifts, ₹${data.pay_amount_inr} per paid lift`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveVendorPolicy = async () => {
    const t = parseInt(vendorPolicy.vendor_offer_timeout_min, 10);
    const m = parseInt(vendorPolicy.vendor_max_misses, 10);
    if (Number.isNaN(t) || t < 5 || t > 10080) { toast.error('Timeout must be 5–10080 minutes'); return; }
    if (Number.isNaN(m) || m < 1 || m > 10) { toast.error('Max misses must be 1–10'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/admin/app-settings/vendor-policy`,
        { vendor_offer_timeout_min: t, vendor_max_misses: m },
        { withCredentials: true });
      toast.success('Vendor offer policy saved');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveProFees = async () => {
    const validateInt = (v, lo, hi, label) => {
      const n = parseInt(v, 10);
      if (Number.isNaN(n) || n < lo || n > hi) { toast.error(`${label} must be ${lo}–${hi}`); return null; }
      return n;
    };
    const pf = validateInt(proFees.pro_confirmation_fee_inr, 0, 100000, 'Pro fee');
    if (pf === null) return;
    const wf = validateInt(proFees.waiter_confirmation_fee_inr, 0, 100000, 'Waiter fee');
    if (wf === null) return;
    const rw = validateInt(proFees.refund_window_days, 0, 90, 'Refund window');
    if (rw === null) return;
    const re = validateInt(proFees.refund_early_pct, 0, 100, 'Early refund %');
    if (re === null) return;
    const rl = validateInt(proFees.refund_late_pct, 0, 100, 'Late refund %');
    if (rl === null) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/app-settings/pro-fees`, {
        pro_confirmation_fee_inr: pf,
        waiter_confirmation_fee_inr: wf,
        refund_window_days: rw,
        refund_early_pct: re,
        refund_late_pct: rl,
      }, { withCredentials: true });
      toast.success('Professional fees & refund policy saved');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSaveAppVersion = async () => {
    const versionRe = /^\d+\.\d+\.\d+$/;
    const minV = (appVersionForm.min_version || '').trim();
    const latestV = (appVersionForm.latest_version || '').trim();
    if (!versionRe.test(minV)) { toast.error('Min version must be MAJOR.MINOR.PATCH (e.g. 1.0.0)'); return; }
    if (latestV && !versionRe.test(latestV)) { toast.error('Latest version must be MAJOR.MINOR.PATCH or blank'); return; }
    const url = (appVersionForm.play_store_url || '').trim();
    if (url && !/^https?:\/\//.test(url)) { toast.error('Play Store URL must start with http(s)://'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/admin/app-settings/min-version`, {
        min_version: minV,
        latest_version: latestV,
        play_store_url: url,
        update_message: (appVersionForm.update_message || '').trim(),
      }, { withCredentials: true });
      toast.success(`Minimum app version set to ${minV}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const runAdminSearch = async (q) => {
    const query = (q ?? searchQuery).trim();
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/search`, {
        params: { q: query },
        withCredentials: true,
      });
      setSearchResults(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
      setSearchResults({ users: [], bookings: [] });
    } finally {
      setSearching(false);
    }
  };

  const handleProcessDeletion = async (reqId) => {
    if (!window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/deletion-requests/${reqId}/process`, {}, { withCredentials: true });
      const counts = data?.deleted ? Object.entries(data.deleted).map(([k, v]) => `${k}:${v}`).join(', ') : '';
      toast.success(`Account deleted${counts ? ` (${counts})` : ''}`);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to process'); }
  };

  const handleRejectDeletion = async (reqId) => {
    if (!window.confirm('Reject this deletion request?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/deletion-requests/${reqId}/reject`, {}, { withCredentials: true });
      toast.success('Request rejected');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reject'); }
  };

  const updateSiteField = (field, value) => {
    setSiteSettings((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleSaveSiteSettings = async () => {
    if (!siteSettings) return;
    // Client-side safety rails
    const required = ['support_email', 'grievance_email', 'legal_email', 'phone', 'whatsapp', 'address', 'jurisdiction_city', 'company_name', 'owner_name'];
    for (const f of required) {
      if (!String(siteSettings[f] || '').trim()) {
        toast.error(`${f.replace(/_/g, ' ')} is required`);
        return;
      }
    }
    setSavingSiteSettings(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/site-settings`, siteSettings, { withCredentials: true });
      setSiteSettings(data.settings);
      // Bust the in-app cache so Footer + legal pages refetch on next render
      try {
        const mod = await import('../hooks/useSiteSettings');
        mod.invalidateSiteSettingsCache?.();
      } catch {}
      toast.success('Site settings updated. Changes are live across the app.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSiteSettings(false);
    }
  };

  const handleReportAction = async (reportId, action) => {
    const labels = { dismiss: 'dismiss this report', hide: 'hide this content from public listings', ban: 'BAN this vendor (suspends their account)' };
    if (!window.confirm(`Are you sure you want to ${labels[action]}?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/reports/${reportId}/${action}`, {}, { withCredentials: true });
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : action === 'hide' ? 'content hidden' : 'vendor banned'}`);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUpdateVendor = async (vendorId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/vendors/${vendorId}`, vendorEditData, {
        withCredentials: true
      });
      toast.success('Vendor updated successfully');
      setEditingVendor(null);
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor and all their services/bookings? This cannot be undone!')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/vendors/${vendorId}`, { withCredentials: true });
      toast.success('Vendor deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer and all their bookings? This cannot be undone!')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/customers/${customerId}`, { withCredentials: true });
      toast.success('Customer deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleImpersonateVendor = async (vendor) => {
    if (vendorImpersonatingId) return;
    setVendorImpersonatingId(vendor.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/impersonate/${vendor.id}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Now logged in as ${vendor.business_name || vendor.name} — use their dashboard Quick Add to seed packages.`);
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonation failed');
      setVendorImpersonatingId(null);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Delete this service and all related bookings? This cannot be undone!')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/services/${serviceId}`, { withCredentials: true });
      toast.success('Service deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, { withCredentials: true });
      toast.success('Booking deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleCleanDatabase = async () => {
    const confirmed = window.prompt(
      'WARNING: This will delete ALL vendors, customers, services, and bookings!\n\nType "DELETE ALL" to confirm:'
    );
    
    if (confirmed !== 'DELETE ALL') {
      toast.error('Database cleanup cancelled');
      return;
    }
    
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/clean-database`, {}, {
        withCredentials: true
      });
      toast.success(`Database cleaned! Deleted: ${data.deleted.users} users, ${data.deleted.services} services, ${data.deleted.bookings} bookings`);
      fetchAllData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const fetchQuickAddItems = async (cat) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/quick-add-items?vendor_category=${cat}`, { withCredentials: true });
      setQuickAddItems(data);
    } catch (error) {
      toast.error('Error loading quick-add items');
    }
  };

  const handleQuickAddCategoryChange = (cat) => {
    setQuickAddCategory(cat);
    fetchQuickAddItems(cat);
    // Reset new item form with defaults for this category
    if (cat === 'caterer') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'menu_item' });
    else if (cat === 'photographer') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'item' });
    else if (cat === 'decorator') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'marriage_type' });
    else if (cat === 'venue') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'venue_type' });
    else if (cat === 'dj') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'dj_type' });
    else if (cat === 'professional') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'pro_type' });
    else if (cat === 'birthday_decorator') setNewItem({ name: '', defaultPrice: '', category: '', item_type: 'birthday_theme' });
  };

  const handleAddQuickItem = async () => {
    if (!newItem.name || !newItem.defaultPrice) { toast.error('Name and price required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/quick-add-items`, {
        vendor_category: quickAddCategory,
        item_type: newItem.item_type,
        category: newItem.category || '',
        name: newItem.name,
        defaultPrice: parseInt(newItem.defaultPrice),
      }, { withCredentials: true });
      toast.success(`${newItem.name} added!`);
      setNewItem({ ...newItem, name: '', defaultPrice: '' });
      fetchQuickAddItems(quickAddCategory);
    } catch (error) {
      toast.error('Error adding item');
    }
  };

  const handleDeleteQuickItem = async (name) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quick-add-items/${encodeURIComponent(name)}?vendor_category=${quickAddCategory}`, { withCredentials: true });
      toast.success(`${name} deleted`);
      fetchQuickAddItems(quickAddCategory);
    } catch (error) {
      toast.error('Error deleting item');
    }
  };

  // Inline-editable price for quick-add items (e.g., admin tweaking Server Package tiers)
  const [editingPrice, setEditingPrice] = useState({ name: null, value: '' });
  const startEditPrice = (item) => setEditingPrice({ name: item.name, value: String(item.defaultPrice ?? '') });
  const cancelEditPrice = () => setEditingPrice({ name: null, value: '' });
  const saveEditPrice = async (item) => {
    const newPrice = parseInt(editingPrice.value, 10);
    if (Number.isNaN(newPrice) || newPrice < 0) {
      toast.error('Enter a valid price');
      return;
    }
    if (newPrice === Number(item.defaultPrice)) {
      cancelEditPrice();
      return;
    }
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/quick-add-items/${encodeURIComponent(item.name)}`,
        { ...item, defaultPrice: newPrice, vendor_category: quickAddCategory },
        { withCredentials: true }
      );
      toast.success(`${item.name} updated to ₹${newPrice.toLocaleString('en-IN')}`);
      cancelEditPrice();
      fetchQuickAddItems(quickAddCategory);
    } catch {
      toast.error('Failed to update price');
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" suffix="Admin" />
          <MobileNav
            links={[]}
            actions={[
              { onClick: logout, label: t('common.logout') || 'Logout', className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ]}
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="mb-12 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none text-text-primary mb-2">
              Admin Dashboard
            </h1>
            <p className="text-base text-text-secondary">Platform Management & Control</p>
          </div>
          <Button
            onClick={handleCleanDatabase}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none self-start md:self-auto"
            data-testid="clean-database-btn"
          >
            <Database size={20} className="mr-2" />
            Clean Database
          </Button>
        </div>

        {/* Global ID search — find any vendor / pro / customer / captain / order by display id, name, phone */}
        <Card className="mb-8 bg-white border-[#E8E1D5]" data-testid="admin-search-card">
          <CardContent className="p-4">
            <form
              onSubmit={(e) => { e.preventDefault(); runAdminSearch(); }}
              className="flex items-center gap-2"
            >
              <MagnifyingGlass size={18} className="text-text-secondary flex-shrink-0" />
              <Input
                type="search"
                placeholder="Search by ID (VND-100001, ORD-100005), name, phone, or email…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-none border-[#E8E1D5] flex-1 font-mono"
                data-testid="admin-search-input"
              />
              <Button type="submit" disabled={searching || searchQuery.trim().length < 2} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="admin-search-btn">
                {searching ? 'Searching…' : 'Search'}
              </Button>
              {searchResults && (
                <Button type="button" variant="outline" onClick={() => { setSearchResults(null); setSearchQuery(''); }} className="rounded-none border-[#E8E1D5]" data-testid="admin-search-clear">
                  Clear
                </Button>
              )}
            </form>
            {searchResults && (
              <div className="mt-4 space-y-3" data-testid="admin-search-results">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                    Users ({searchResults.users.length})
                  </div>
                  {searchResults.users.length === 0 ? (
                    <p className="text-sm text-text-secondary italic">No matching users</p>
                  ) : (
                    <div className="space-y-1.5">
                      {searchResults.users.map((u) => (
                        <div key={u.id} className="flex items-center gap-2 flex-wrap p-2 border border-[#E8E1D5] rounded bg-sand-50" data-testid={`admin-search-user-${u.id}`}>
                          {u.display_id && <IdBadge id={u.display_id} testId={`admin-search-user-id-${u.id}`} />}
                          <span className="text-sm font-medium text-text-primary">{u.business_name || u.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-brand-primary/10 text-brand-primary uppercase tracking-wider font-bold rounded">{u.role}{u.category ? ` · ${u.category}` : ''}</span>
                          {u.phone && <span className="text-xs text-text-secondary">{u.phone}</span>}
                          {u.email && <span className="text-xs text-text-secondary">{u.email}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                    Bookings ({searchResults.bookings.length})
                  </div>
                  {searchResults.bookings.length === 0 ? (
                    <p className="text-sm text-text-secondary italic">No matching bookings</p>
                  ) : (
                    <div className="space-y-1.5">
                      {searchResults.bookings.map((b) => (
                        <div key={b.id} className="flex items-center gap-2 flex-wrap p-2 border border-[#E8E1D5] rounded bg-sand-50" data-testid={`admin-search-booking-${b.id}`}>
                          {b.display_id && <IdBadge id={b.display_id} testId={`admin-search-booking-id-${b.id}`} />}
                          <span className="text-sm font-medium text-text-primary">{b.package_name || 'Package Booking'}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-brand-primary/10 text-brand-primary uppercase tracking-wider font-bold rounded">{b.status}</span>
                          {b.customer_name && <span className="text-xs text-text-secondary">{b.customer_name}</span>}
                          <span className="text-xs text-text-secondary ml-auto">₹{Number(b.total || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'quickadd') handleQuickAddCategoryChange(quickAddCategory); }} className="w-full">
          <TabsList className="flex w-full overflow-x-auto flex-nowrap gap-1 mb-8 h-auto p-1 justify-start md:grid md:grid-cols-14">
            <TabsTrigger value="stats" className="whitespace-nowrap flex-shrink-0">{t('admin.statistics')}</TabsTrigger>
            <TabsTrigger value="vendors" className="whitespace-nowrap flex-shrink-0">{t('admin.vendors')}</TabsTrigger>
            <TabsTrigger value="pros" className="whitespace-nowrap flex-shrink-0" data-testid="admin-pros-tab">Professionals</TabsTrigger>
            <TabsTrigger value="customers" className="whitespace-nowrap flex-shrink-0">{t('admin.customers')}</TabsTrigger>
            <TabsTrigger value="services" className="whitespace-nowrap flex-shrink-0">{t('admin.services')}</TabsTrigger>
            <TabsTrigger value="bookings" className="whitespace-nowrap flex-shrink-0">{t('admin.bookings')}</TabsTrigger>
            <TabsTrigger value="pkgbookings" className="whitespace-nowrap flex-shrink-0" data-testid="admin-pkg-bookings-tab">Package Bookings</TabsTrigger>
            <TabsTrigger value="captains" className="whitespace-nowrap flex-shrink-0" data-testid="admin-captains-tab">Captains</TabsTrigger>
            <TabsTrigger value="calendar" className="whitespace-nowrap flex-shrink-0" data-testid="admin-calendar-tab">Calendar</TabsTrigger>
            <TabsTrigger value="quickadd" className="whitespace-nowrap flex-shrink-0" data-testid="admin-quickadd-tab">{t('admin.quick_add')}</TabsTrigger>
            <TabsTrigger value="withdrawals" className="whitespace-nowrap flex-shrink-0" data-testid="admin-withdrawals-tab">{t('admin.withdrawals')}</TabsTrigger>
            <TabsTrigger value="kyc" className="whitespace-nowrap flex-shrink-0" data-testid="admin-kyc-tab">KYC</TabsTrigger>
            <TabsTrigger value="templates" className="whitespace-nowrap flex-shrink-0" data-testid="admin-templates-tab">Templates</TabsTrigger>
            <TabsTrigger value="segments" className="whitespace-nowrap flex-shrink-0" data-testid="admin-segments-tab">Pricing Segments</TabsTrigger>
            <TabsTrigger value="deletion" className="whitespace-nowrap flex-shrink-0" data-testid="admin-deletion-tab">Deletion Requests</TabsTrigger>
            <TabsTrigger value="reports" className="whitespace-nowrap flex-shrink-0" data-testid="admin-reports-tab">
              Reports
              {reports.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-600 text-white rounded-full px-1">
                  {reports.filter(r => r.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="site" className="whitespace-nowrap flex-shrink-0" data-testid="admin-site-tab">Site Settings</TabsTrigger>
            <TabsTrigger value="heatmaps" className="whitespace-nowrap flex-shrink-0" data-testid="admin-heatmaps-tab">Heatmaps</TabsTrigger>
          </TabsList>

          {/* Statistics Tab */}
          <TabsContent value="stats">
            {loading ? (
              <div className="text-center py-16">Loading statistics...</div>
            ) : stats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-white border-[#E8E1D5]">
                  <CardHeader>
                    <CardDescription className="flex items-center gap-2">
                      <Users size={20} weight="duotone" className="text-brand-primary" />
                      <span className="text-xs tracking-[0.2em] uppercase font-bold">Total Users</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-5xl text-text-primary">{stats.total_users}</div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E8E1D5]">
                  <CardHeader>
                    <CardDescription className="flex items-center gap-2">
                      <Users size={20} weight="duotone" className="text-brand-secondary" />
                      <span className="text-xs tracking-[0.2em] uppercase font-bold">Vendors</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-5xl text-text-primary">{stats.total_vendors}</div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E8E1D5]">
                  <CardHeader>
                    <CardDescription className="flex items-center gap-2">
                      <Package size={20} weight="duotone" className="text-brand-primary" />
                      <span className="text-xs tracking-[0.2em] uppercase font-bold">Services</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-5xl text-text-primary">{stats.total_services}</div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#E8E1D5]">
                  <CardHeader>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar size={20} weight="duotone" className="text-brand-primary" />
                      <span className="text-xs tracking-[0.2em] uppercase font-bold">Bookings</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-5xl text-text-primary">{stats.total_bookings}</div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-16">Error loading statistics</div>
            )}
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <div className="space-y-4">
              {/* Filter chips */}
              {vendors.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="admin-vendors-filter-row">
                  {(() => {
                    const emptyCount = vendors.filter((v) => v.is_empty_profile).length;
                    return [
                      ['all', 'All', vendors.length],
                      ['empty', 'Empty Profile', emptyCount],
                    ].map(([k, label, count]) => (
                      <button key={k} onClick={() => setVendorFilter(k)}
                        className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border rounded-none ${vendorFilter === k ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'border-[#E8E1D5] text-text-secondary hover:bg-stone-100'} ${k === 'empty' && count > 0 && vendorFilter !== k ? 'border-amber-400 text-amber-700' : ''}`}
                        data-testid={`admin-vendors-filter-${k}`}>
                        {label} <span className="ml-1 opacity-70">({count})</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
              {vendors.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No vendors found</p>
                  </CardContent>
                </Card>
              ) : (
                vendors
                  .filter((vendor) => vendorFilter === 'all' || (vendorFilter === 'empty' && vendor.is_empty_profile))
                  .map((vendor) => (
                  <Card key={vendor.id} className="bg-white border-[#E8E1D5]">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {editingVendor === vendor.id ? (
                            <div className="space-y-3">
                              <Input
                                value={vendorEditData.name}
                                onChange={(e) => setVendorEditData({ ...vendorEditData, name: e.target.value })}
                                placeholder="Name"
                                className="rounded-none border-[#E8E1D5]"
                              />
                              <Input
                                value={vendorEditData.business_name}
                                onChange={(e) => setVendorEditData({ ...vendorEditData, business_name: e.target.value })}
                                placeholder="Business Name"
                                className="rounded-none border-[#E8E1D5]"
                              />
                              <Input
                                value={vendorEditData.location}
                                onChange={(e) => setVendorEditData({ ...vendorEditData, location: e.target.value })}
                                placeholder="Location"
                                className="rounded-none border-[#E8E1D5]"
                              />
                              <Input
                                value={vendorEditData.description}
                                onChange={(e) => setVendorEditData({ ...vendorEditData, description: e.target.value })}
                                placeholder="Description"
                                className="rounded-none border-[#E8E1D5]"
                              />
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleUpdateVendor(vendor.id)}
                                  className="bg-brand-secondary hover:bg-[#3A4D3E] text-sand-50 rounded-none"
                                >
                                  Save
                                </Button>
                                <Button
                                  onClick={() => setEditingVendor(null)}
                                  className="bg-transparent border border-text-primary text-text-primary hover:bg-text-primary hover:text-sand-50 rounded-none"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="font-heading text-2xl">{vendor.business_name || vendor.name}</CardTitle>
                                {vendor.is_empty_profile && (
                                  <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-500 text-white px-2 py-0.5 rounded" data-testid={`admin-vendor-empty-${vendor.id}`}>
                                    Empty Profile
                                  </span>
                                )}
                              </div>
                              <CardDescription className="capitalize">{vendor.category} • {vendor.location}</CardDescription>
                              <p className="text-sm text-text-secondary mt-2">Phone: {vendor.phone}</p>
                            </>
                          )}
                        </div>
                        {editingVendor !== vendor.id && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleImpersonateVendor(vendor)}
                              disabled={!!vendorImpersonatingId}
                              size="sm"
                              className="bg-rose-700 hover:bg-rose-800 text-sand-50 rounded-none h-8 px-2 text-[10px] tracking-wider uppercase font-bold inline-flex items-center gap-1"
                              data-testid={`admin-vendor-impersonate-${vendor.id}`}
                              title="Log in as this vendor to seed packages on their behalf"
                            >
                              <UserCircleGear size={12} />
                              {vendorImpersonatingId === vendor.id ? 'Opening…' : 'Impersonate'}
                            </Button>
                            <Button
                              onClick={() => handleEditVendor(vendor)}
                              variant="ghost"
                              size="sm"
                              className="text-brand-primary hover:text-brand-primary hover:bg-brand-accent/20"
                            >
                              <Pencil size={18} />
                            </Button>
                            <Button
                              onClick={() => handleDeleteVendor(vendor.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash size={18} />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Professionals + Entertainment Pros Tab */}
          <TabsContent value="pros">
            <AdminProfessionalsPanel />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="space-y-4">
              {customers.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No customers found</p>
                  </CardContent>
                </Card>
              ) : (
                customers.map((customer) => (
                  <Card key={customer.id} className="bg-white border-[#E8E1D5]">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="font-heading text-xl">{customer.name}</CardTitle>
                          <CardDescription>Phone: {customer.phone}</CardDescription>
                        </div>
                        <Button
                          onClick={() => handleDeleteCustomer(customer.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="space-y-4">
              {services.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No services found</p>
                  </CardContent>
                </Card>
              ) : (
                services.map((service) => (
                  <Card key={service.id} className="bg-white border-[#E8E1D5]">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="font-heading text-xl">{service.name}</CardTitle>
                          <CardDescription>
                            By: {service.vendor_business_name || service.vendor_name} • ₹{service.base_price}
                          </CardDescription>
                          <p className="text-sm text-text-secondary mt-2">{service.description}</p>
                        </div>
                        <Button
                          onClick={() => handleDeleteService(service.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-text-secondary">No bookings found</p>
                  </CardContent>
                </Card>
              ) : (
                bookings.map((booking) => (
                  <Card key={booking.id} className="bg-white border-[#E8E1D5]">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="font-heading text-xl">{booking.service_name}</CardTitle>
                          <CardDescription>
                            Customer: {booking.customer_name} ({booking.customer_phone}) • 
                            Vendor: {booking.vendor_business_name || booking.vendor_name}
                          </CardDescription>
                          <div className="flex gap-4 mt-2 text-sm text-text-secondary">
                            <span>Date: {new Date(booking.event_date).toLocaleDateString()}</span>
                            <span>Price: ₹{booking.total_price}</span>
                            <span className="capitalize">Status: {booking.status}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDeleteBooking(booking.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Package Bookings Tab */}
          <TabsContent value="pkgbookings">
            <div className="space-y-4">
              {packageBookings.length === 0 ? (
                <Card className="bg-white border-[#E8E1D5]">
                  <CardContent className="py-12 text-center">
                    <p className="text-base text-text-secondary">No package bookings yet.</p>
                  </CardContent>
                </Card>
              ) : (
                packageBookings.map(pb => (
                  <Card key={pb.id} className="bg-white border-[#E8E1D5]" data-testid={`admin-pkg-booking-${pb.id}`}>
                    <CardHeader>
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                        <div>
                          <CardTitle className="font-heading text-xl">{pb.package_name || 'Event Package'}</CardTitle>
                          <CardDescription className="mt-1 capitalize">
                            {pb.event_type}{pb.marriage_type ? ` — ${pb.marriage_type}` : ''} • {pb.guests} guests • {pb.event_date}
                          </CardDescription>
                          <div className="mt-2 text-sm text-text-secondary">
                            <strong className="text-text-primary">{pb.customer_name}</strong> • {pb.customer_phone}
                            {pb.event_location && <> • {pb.event_location}</>}
                          </div>
                          {pb.notes && <div className="mt-1 text-sm italic text-text-secondary">"{pb.notes}"</div>}
                        </div>
                        <div className="text-left md:text-right flex-shrink-0">
                          <div className="font-heading text-2xl text-brand-primary">₹{Number(pb.total).toLocaleString('en-IN')}</div>
                          <span className={`inline-block text-xs px-2 py-1 rounded font-bold uppercase mt-1 ${pb.status === 'confirmed' ? 'bg-green-100 text-green-700' : pb.status === 'pending' ? 'bg-amber-100 text-amber-700' : pb.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : pb.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-text-secondary'}`}>
                            {pb.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <button onClick={() => setExpandedPkgBooking(expandedPkgBooking === pb.id ? null : pb.id)} className="text-sm text-brand-primary hover:underline mb-3" data-testid={`expand-pkg-booking-${pb.id}`}>
                        {expandedPkgBooking === pb.id ? 'Hide items' : `View ${pb.items?.length || 0} items`}
                      </button>
                      {expandedPkgBooking === pb.id && pb.items?.length > 0 && (
                        <div className="space-y-1 mb-4 border border-[#E8E1D5] rounded p-3 bg-sand-50">
                          {pb.items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm py-1">
                              <span>{item.name} {item.qty > 1 ? `× ${item.qty}` : ''}</span>
                              <span className="font-bold text-brand-primary">₹{Number(item.price).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E8E1D5]">
                        {['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map(s => (
                          <Button
                            key={s}
                            onClick={() => handleUpdatePackageBookingStatus(pb.id, s)}
                            disabled={pb.status === s}
                            size="sm"
                            variant={pb.status === s ? 'default' : 'outline'}
                            className={`rounded-none ${pb.status === s ? 'bg-brand-primary text-sand-50' : 'border-[#E8E1D5]'} capitalize`}
                            data-testid={`set-status-${s}-${pb.id}`}
                          >
                            {s.replace('_', ' ')}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Captains Tab */}
          <TabsContent value="captains">
            <div className="space-y-6">
              {/* Create captain */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="create-captain-card">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Create Captain Account</CardTitle>
                  <CardDescription>Captains are auto-assigned (by priority) to confirmed package bookings. They oversee event attendance.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Phone (with +91)</Label>
                      <Input type="text" value={newCaptain.phone} onChange={(e) => setNewCaptain({ ...newCaptain, phone: e.target.value })} placeholder="+919999900000" className="rounded-none border-[#E8E1D5] h-9" data-testid="cap-phone-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Name</Label>
                      <Input type="text" value={newCaptain.name} onChange={(e) => setNewCaptain({ ...newCaptain, name: e.target.value })} placeholder="Captain name" className="rounded-none border-[#E8E1D5] h-9" data-testid="cap-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Base Location</Label>
                      <Input type="text" value={newCaptain.location} onChange={(e) => setNewCaptain({ ...newCaptain, location: e.target.value })} placeholder="Coimbatore" className="rounded-none border-[#E8E1D5] h-9" data-testid="cap-location-input" />
                    </div>
                  </div>
                  <Button onClick={handleCreateCaptain} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="create-captain-btn">
                    Create Captain
                  </Button>
                </CardContent>
              </Card>

              {/* Geofence radius */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="geofence-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Geofence Radius</CardTitle>
                  <CardDescription>Distance (in meters) within which professionals must be to auto-check-in.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-3">
                    <div className="space-y-1 max-w-[200px]">
                      <Label className="text-xs font-bold text-text-secondary">Radius (meters, 50–5000)</Label>
                      <Input type="number" value={geofenceRadius} onChange={(e) => setGeofenceRadius(e.target.value)} className="rounded-none border-[#E8E1D5] h-9" data-testid="geofence-input" />
                    </div>
                    <Button onClick={handleSaveGeofence} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-geofence-btn">Save</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Waiter offer timeout */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="waiter-timeout-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Waiter Order Offer Timeout</CardTitle>
                  <CardDescription>How many minutes a Server / Waiter has to confirm an order before it auto-rolls to the next person in queue. Default 30 min.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-3">
                    <div className="space-y-1 max-w-[200px]">
                      <Label className="text-xs font-bold text-text-secondary">Minutes (1–1440)</Label>
                      <Input type="number" min={1} max={1440} value={waiterTimeoutMin} onChange={(e) => setWaiterTimeoutMin(e.target.value)} className="rounded-none border-[#E8E1D5] h-9" data-testid="waiter-timeout-input" />
                    </div>
                    <Button onClick={handleSaveWaiterTimeout} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-waiter-timeout-btn">Save</Button>
                  </div>
                  <p className="text-xs text-text-secondary mt-2">Applies to all new offers. Existing pending offers keep their original timer.</p>
                </CardContent>
              </Card>

              {/* Service Cities — admin-curated master list */}
              <AdminServiceCitiesCard />

              {/* Per-role media library caps */}
              <AdminMediaCapsCard />
              <AdminModerationToggleCard />
              <AdminIntegrationKeysCard />
              <AdminInviteEarnToggleCard />

              {/* SEO image renaming scheme */}
              <AdminImageNamingCard />

              {/* SEO city list — drives /api/sitemap.xml */}
              <AdminSEOCitiesCard />

              {/* SEO advanced — GSC verification + IndexNow auto-submission */}
              <AdminSEOAdvancedCard />

              {/* AI vendor search — kill-switch + daily quota */}
              <AdminAISearchCard />

              {/* Razorpay payments — kill-switch + recent intents */}
              <AdminRazorpayCard />

              {/* Instant Pricing segment banner images */}
              <AdminSegmentImagesCard />

              {/* ---- Web-only marketing tools ------------------------------- *
               *  Hidden in the Android APK to keep the Play Store Data-Safety  *
               *  declaration clean (these scrape & store third-party business  *
               *  phone numbers via Google Places). On web they remain fully    *
               *  available to admins as B2B outreach tools.                    */}

              {/* Magic Invite Links — admin-issued, OTP-less signup links.
                  Safe for native too: no scraping, just creates a token. */}
              <AdminMagicLinkCard />

              {!isNativeApp() && (
                <>
                  {/* Marketing — WhatsApp invites to prospective vendors / pros */}
                  <AdminMarketingInviteCard />

                  {/* Auto-blast — city-wise, segment-proportional WhatsApp blast */}
                  <AdminAutoBlastCard />

                  {/* Contact Finder — Google Maps Places phone-number discovery */}
                  <AdminContactFinderCard />

                  {/* Google Web Search Finder — free-text keyword search via SerpAPI */}
                  <AdminGoogleSearchCard />

                  {/* Segment name-keyword rules — drop off-topic contacts on save */}
                  <AdminSegmentRulesCard />
                </>
              )}

              {/* Vendor Offer Policy */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-policy-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Vendor Offer Policy</CardTitle>
                  <CardDescription>
                    When the customer hits <span className="font-semibold">"Get Vendor Phone Numbers"</span> we send offers to each matched vendor. They have this long to accept, and after <span className="font-semibold">N consecutive misses</span> (decline / ignore / expire) the vendor's calendar is auto-blocked for the event date.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Response Window (minutes)</Label>
                      <Input type="number" min={5} max={10080} value={vendorPolicy.vendor_offer_timeout_min}
                        onChange={(e) => setVendorPolicy({ ...vendorPolicy, vendor_offer_timeout_min: e.target.value })}
                        className="rounded-none border-[#E8E1D5] h-9"
                        data-testid="vendor-timeout-input" />
                      <p className="text-[10px] text-text-secondary">5–10080 min. Default 1440 (24h).</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Max Misses Before Auto-Block</Label>
                      <Input type="number" min={1} max={10} value={vendorPolicy.vendor_max_misses}
                        onChange={(e) => setVendorPolicy({ ...vendorPolicy, vendor_max_misses: e.target.value })}
                        className="rounded-none border-[#E8E1D5] h-9"
                        data-testid="vendor-max-misses-input" />
                      <p className="text-[10px] text-text-secondary">1–10. Default 3.</p>
                    </div>
                  </div>
                  <div className="flex justify-end pt-3">
                    <Button onClick={handleSaveVendorPolicy} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-vendor-policy-btn">
                      Save Vendor Policy
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* App Version Gate */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="app-version-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">App Version Gate</CardTitle>
                  <CardDescription>
                    Force users on older builds to upgrade. Any installed app below <span className="font-semibold">Min Version</span> sees a blocking screen with the Play Store link until they update.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Minimum Required Version *</Label>
                      <Input
                        type="text"
                        placeholder="e.g. 1.0.0"
                        value={appVersionForm.min_version}
                        onChange={(e) => setAppVersionForm({ ...appVersionForm, min_version: e.target.value })}
                        className="rounded-none border-[#E8E1D5] h-9 font-mono"
                        data-testid="app-min-version-input"
                      />
                      <p className="text-[10px] text-text-secondary">MAJOR.MINOR.PATCH — anything below is blocked.</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Latest Available Version (optional)</Label>
                      <Input
                        type="text"
                        placeholder="e.g. 1.2.0"
                        value={appVersionForm.latest_version}
                        onChange={(e) => setAppVersionForm({ ...appVersionForm, latest_version: e.target.value })}
                        className="rounded-none border-[#E8E1D5] h-9 font-mono"
                        data-testid="app-latest-version-input"
                      />
                      <p className="text-[10px] text-text-secondary">Shown to users on the update screen.</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">App Download URL</Label>
                    <Input
                      type="url"
                      placeholder="https://beeweddy.com/download/beeweddy.apk"
                      value={appVersionForm.play_store_url}
                      onChange={(e) => setAppVersionForm({ ...appVersionForm, play_store_url: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid="app-playstore-url-input"
                    />
                    <p className="text-[10px] text-text-secondary">Paste your hosted APK / Play Store / App Store link — shown on the Create Account page Download button.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Update Message (optional)</Label>
                    <Input
                      type="text"
                      placeholder="A newer version of BeeWeddy is available. Please update to continue."
                      value={appVersionForm.update_message}
                      onChange={(e) => setAppVersionForm({ ...appVersionForm, update_message: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9"
                      maxLength={500}
                      data-testid="app-update-message-input"
                    />
                  </div>
                  <div className="flex justify-end pt-1">
                    <Button onClick={handleSaveAppVersion} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-app-version-btn">
                      Save Version Gate
                    </Button>
                  </div>
                  <p className="text-[11px] text-text-secondary border-l-2 border-brand-primary/30 pl-2">
                    Tip: set Min Version to <span className="font-mono">0.0.0</span> to disable the gate entirely.
                  </p>
                </CardContent>
              </Card>

              <AdminAppApkCard />

              {/* List captains */}
              <Card className="bg-white border-[#E8E1D5]" data-testid="captains-list-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Captain Roster ({captains.length})</CardTitle>
                  <CardDescription>Lower priority number = picked first. Toggle availability to skip a captain in the rotation.</CardDescription>
                </CardHeader>
                <CardContent>
                  {captains.length === 0 ? (
                    <p className="text-sm text-text-secondary">No captains yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {captains.map((c) => (
                        <div key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-[#E8E1D5] rounded" data-testid={`captain-row-${c.id}`}>
                          <div className="min-w-0">
                            <div className="font-medium text-sm">{c.name} <span className="text-xs text-text-secondary">• {c.phone}</span></div>
                            <div className="text-xs text-text-secondary">{c.location || '—'}</div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Label className="text-[10px] uppercase font-bold text-text-secondary">Priority</Label>
                            <Input type="number" defaultValue={c.priority} onBlur={(e) => {
                              const v = parseInt(e.target.value, 10);
                              if (v && v !== c.priority) handleUpdateCaptain(c.id, { priority: v });
                            }} className="w-16 h-8 rounded-none border-[#E8E1D5]" data-testid={`captain-priority-${c.id}`} />
                            <Button onClick={() => handleUpdateCaptain(c.id, { available: !c.available })} size="sm" variant="outline" className={`rounded-none border-[#E8E1D5] ${c.available ? 'text-green-700' : 'text-amber-700'}`} data-testid={`toggle-cap-${c.id}`}>
                              {c.available ? 'Available' : 'Unavailable'}
                            </Button>
                            <Button onClick={() => handleDeleteCaptain(c.id)} size="sm" variant="ghost" className="text-red-500" data-testid={`delete-cap-${c.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Calendar Tab — Rules: muhurtham coverage + lead caps + lift fee */}
          <TabsContent value="calendar">
            <div className="space-y-6">
              <AdminCalendarRulesCard />
              <AdminLeadPacksPanel />
            </div>
          </TabsContent>

          {/* Quick Add Management Tab */}
          <TabsContent value="quickadd">
            <Card className="bg-white border-[#E8E1D5]">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Manage Quick Add Items</CardTitle>
                <CardDescription>Add, edit or delete items that vendors see in their Quick Add menus</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Category Selector */}
                <div className="flex gap-2">
                  {['caterer', 'photographer', 'decorator', 'birthday_decorator', 'venue', 'dj', 'professional'].map(cat => (
                    <Button
                      key={cat}
                      onClick={() => handleQuickAddCategoryChange(cat)}
                      className={`rounded-none capitalize ${quickAddCategory === cat ? 'bg-brand-primary text-sand-50' : 'bg-[#E8E1D5] text-text-primary'}`}
                      data-testid={`qa-cat-${cat}`}
                    >
                      {cat === 'birthday_decorator' ? 'Birthday Deco' : cat}
                    </Button>
                  ))}
                </div>

                {/* Add New Item Form */}
                <div className="p-4 border-2 border-dashed border-brand-primary/30 bg-brand-accent/5">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-3">Add New Item</div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <Input
                      placeholder="Item Name"
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      className="rounded-none border-[#E8E1D5]"
                      data-testid="qa-new-name"
                    />
                    <Input
                      type="number"
                      placeholder="Price (₹)"
                      value={newItem.defaultPrice}
                      onChange={(e) => setNewItem({ ...newItem, defaultPrice: e.target.value })}
                      className="rounded-none border-[#E8E1D5]"
                      data-testid="qa-new-price"
                    />
                    <Input
                      placeholder="Sub-category"
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                      className="rounded-none border-[#E8E1D5]"
                      data-testid="qa-new-category"
                    />
                    {quickAddCategory === 'decorator' && (
                      <select
                        value={newItem.item_type}
                        onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value })}
                        className="rounded-none border border-[#E8E1D5] px-3 py-2 text-sm bg-white"
                        data-testid="qa-new-type"
                      >
                        <option value="marriage_type">Marriage Type</option>
                        <option value="venue_type">Venue Type</option>
                        <option value="decoration_service">Decoration Service</option>
                      </select>
                    )}
                    {quickAddCategory === 'venue' && (
                      <select
                        value={newItem.item_type}
                        onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value })}
                        className="rounded-none border border-[#E8E1D5] px-3 py-2 text-sm bg-white"
                        data-testid="qa-new-type-venue"
                      >
                        <option value="venue_type">Venue Type</option>
                      </select>
                    )}
                    {quickAddCategory === 'dj' && (
                      <select
                        value={newItem.item_type}
                        onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value })}
                        className="rounded-none border border-[#E8E1D5] px-3 py-2 text-sm bg-white"
                        data-testid="qa-new-type-dj"
                      >
                        <option value="dj_type">Entertainment Type</option>
                        <option value="equipment">Equipment</option>
                        <option value="genre">Genre</option>
                      </select>
                    )}
                    {quickAddCategory === 'professional' && (
                      <select
                        value={newItem.item_type}
                        onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value })}
                        className="rounded-none border border-[#E8E1D5] px-3 py-2 text-sm bg-white"
                        data-testid="qa-new-type-professional"
                      >
                        <option value="pro_type">Professional Type</option>
                        <option value="server_package">Server Salary Package</option>
                      </select>
                    )}
                    {quickAddCategory === 'birthday_decorator' && (
                      <select
                        value={newItem.item_type}
                        onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value })}
                        className="rounded-none border border-[#E8E1D5] px-3 py-2 text-sm bg-white"
                        data-testid="qa-new-type-birthday-deco"
                      >
                        <option value="birthday_theme">Birthday Theme</option>
                        <option value="birthday_addon">Add-on Decoration</option>
                      </select>
                    )}
                    <Button
                      onClick={handleAddQuickItem}
                      className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                      data-testid="qa-add-btn"
                    >
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Items List */}
                <div className="space-y-1">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                    {quickAddItems.length} Items for {quickAddCategory}
                  </div>
                  {quickAddItems.length === 0 ? (
                    <p className="text-sm text-text-secondary py-4">No items yet. Add one above or restart server to seed defaults.</p>
                  ) : (
                    (() => {
                      const grouped = {};
                      quickAddItems.forEach(item => {
                        const key = item.item_type || item.category || 'General';
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(item);
                      });
                      return Object.entries(grouped).map(([group, items]) => (
                        <div key={group} className="mb-4">
                          <div className="text-sm font-bold text-text-secondary capitalize mb-1">{group.replace(/_/g, ' ')}</div>
                          <div className="space-y-1">
                            {items.map((item, idx) => {
                              const isEditing = editingPrice.name === item.name;
                              return (
                              <div key={idx} className="flex items-center gap-3 p-2 border border-[#E8E1D5] bg-white rounded">
                                <span className="flex-1 text-sm font-medium">{item.name}</span>
                                {item.category && <span className="text-xs text-text-secondary bg-[#E8E1D5]/50 px-2 py-0.5 rounded">{item.category}</span>}
                                {isEditing ? (
                                  <div className="flex items-center gap-1" data-testid={`qa-edit-${item.name.toLowerCase().replace(/ /g, '-')}`}>
                                    <span className="text-sm text-brand-primary">₹</span>
                                    <input
                                      type="number"
                                      value={editingPrice.value}
                                      onChange={(e) => setEditingPrice({ ...editingPrice, value: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEditPrice(item);
                                        if (e.key === 'Escape') cancelEditPrice();
                                      }}
                                      autoFocus
                                      className="w-24 px-2 py-1 text-sm font-semibold text-brand-primary border border-brand-primary rounded-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                      data-testid={`qa-edit-input-${item.name.toLowerCase().replace(/ /g, '-')}`}
                                    />
                                    <Button onClick={() => saveEditPrice(item)} variant="ghost" size="sm" className="text-green-600 hover:bg-green-50" data-testid={`qa-save-${item.name.toLowerCase().replace(/ /g, '-')}`}>
                                      ✓
                                    </Button>
                                    <Button onClick={cancelEditPrice} variant="ghost" size="sm" className="text-text-secondary hover:bg-[#E8E1D5]/30">
                                      ✕
                                    </Button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => startEditPrice(item)}
                                    className="text-sm font-semibold text-brand-primary hover:bg-brand-primary/5 px-2 py-1 rounded transition-colors"
                                    title="Click to edit price"
                                    data-testid={`qa-price-${item.name.toLowerCase().replace(/ /g, '-')}`}
                                  >
                                    ₹{Number(item.defaultPrice).toLocaleString('en-IN')}
                                  </button>
                                )}
                                <Button
                                  onClick={() => handleDeleteQuickItem(item.name)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`qa-delete-${item.name.toLowerCase().replace(/ /g, '-')}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WITHDRAWALS TAB */}
          <TabsContent value="withdrawals">
            {/* Referral Settings */}
            <Card className="bg-white border-[#E8E1D5] mb-6" data-testid="referral-settings-card">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="font-heading text-2xl">Invite &amp; Earn Settings</CardTitle>
                    <CardDescription>Configure invite bonus, withdrawal gate, and per-tier commission &amp; streak targets</CardDescription>
                  </div>
                  <Button onClick={() => setSettingsEditing(!settingsEditing)} variant="outline" size="sm" className="rounded-none border-[#E8E1D5]" data-testid="edit-referral-settings-btn">
                    {settingsEditing ? 'Cancel' : 'Edit Settings'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {settingsEditing ? (
                  <div className="space-y-5">
                    {/* Global knobs */}
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2">Global</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs font-bold text-text-secondary">Invite Bonus (₹)</Label>
                          <Input type="number" value={referralSettings.invite_bonus_amount ?? 50}
                            onChange={(e) => setReferralSettings({ ...referralSettings, invite_bonus_amount: e.target.value })}
                            className="rounded-none border-[#E8E1D5] h-9" data-testid="setting-invite-bonus" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-bold text-text-secondary">Clawback After (days)</Label>
                          <Input type="number" value={referralSettings.invite_bonus_clawback_days ?? 90}
                            onChange={(e) => setReferralSettings({ ...referralSettings, invite_bonus_clawback_days: e.target.value })}
                            className="rounded-none border-[#E8E1D5] h-9" data-testid="setting-clawback-days" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-bold text-text-secondary">Min Invites to Withdraw</Label>
                          <Input type="number" value={referralSettings.withdrawal_min_invites ?? 10}
                            onChange={(e) => setReferralSettings({ ...referralSettings, withdrawal_min_invites: e.target.value })}
                            className="rounded-none border-[#E8E1D5] h-9" data-testid="setting-withdraw-min" />
                        </div>
                      </div>
                    </div>

                    {/* Per-tier */}
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2">Per Invitee Type</div>
                      <div className="space-y-3">
                        {Object.entries(referralSettings.tiers || {}).map(([tierKey, tierVal]) => (
                          <div key={tierKey} className="border border-[#E8E1D5] p-3 rounded" data-testid={`tier-setting-${tierKey}`}>
                            <div className="text-sm font-bold text-text-primary mb-2 capitalize">{tierKey.replace('_', ' — ')}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-[10px] font-bold text-text-secondary uppercase">Commission %</Label>
                                <Input type="number" value={tierVal.commission_percentage ?? 0}
                                  onChange={(e) => setReferralSettings({
                                    ...referralSettings,
                                    tiers: { ...referralSettings.tiers, [tierKey]: { ...tierVal, commission_percentage: e.target.value } }
                                  })}
                                  className="rounded-none border-[#E8E1D5] h-9" data-testid={`tier-${tierKey}-pct`} />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px] font-bold text-text-secondary uppercase">Required Invites</Label>
                                <Input type="number" value={tierVal.required_invites ?? 0}
                                  onChange={(e) => setReferralSettings({
                                    ...referralSettings,
                                    tiers: { ...referralSettings.tiers, [tierKey]: { ...tierVal, required_invites: e.target.value } }
                                  })}
                                  className="rounded-none border-[#E8E1D5] h-9" data-testid={`tier-${tierKey}-required`} />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px] font-bold text-text-secondary uppercase">Period (days, 0 = no streak)</Label>
                                <Input type="number" value={tierVal.period_days ?? 0}
                                  onChange={(e) => setReferralSettings({
                                    ...referralSettings,
                                    tiers: { ...referralSettings.tiers, [tierKey]: { ...tierVal, period_days: e.target.value } }
                                  })}
                                  className="rounded-none border-[#E8E1D5] h-9" data-testid={`tier-${tierKey}-period`} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Platform Commission (markup added on Instant Pricing prices) */}
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2">Platform Commission (markup on Instant Pricing)</div>
                      <p className="text-xs text-text-secondary mb-2">Added on top of vendor / venue / professional listing prices shown to customers in Instant Pricing. Vendor still receives their listed amount.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {['professional', 'vendor', 'venue'].map((grp) => (
                          <div key={grp} className="border border-[#E8E1D5] p-3 rounded" data-testid={`pc-setting-${grp}`}>
                            <Label className="text-[10px] font-bold text-text-secondary uppercase capitalize block mb-1">{grp} Markup %</Label>
                            <Input type="number" value={referralSettings.platform_commission?.[grp] ?? 0}
                              onChange={(e) => setReferralSettings({
                                ...referralSettings,
                                platform_commission: {
                                  ...(referralSettings.platform_commission || {}),
                                  [grp]: e.target.value,
                                }
                              })}
                              className="rounded-none border-[#E8E1D5] h-9" data-testid={`pc-${grp}-input`} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button onClick={handleSaveReferralSettings} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-referral-settings-btn">
                      Save Settings
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="text-center border border-[#E8E1D5] rounded p-3">
                        <div className="font-heading text-2xl font-bold text-brand-primary">₹{referralSettings.invite_bonus_amount ?? 50}</div>
                        <div className="text-xs text-text-secondary mt-1">Invite Bonus</div>
                      </div>
                      <div className="text-center border border-[#E8E1D5] rounded p-3">
                        <div className="font-heading text-2xl font-bold text-text-primary">{referralSettings.invite_bonus_clawback_days ?? 90} d</div>
                        <div className="text-xs text-text-secondary mt-1">Clawback Window</div>
                      </div>
                      <div className="text-center border border-[#E8E1D5] rounded p-3">
                        <div className="font-heading text-2xl font-bold text-text-primary">{referralSettings.withdrawal_min_invites ?? 10}</div>
                        <div className="text-xs text-text-secondary mt-1">Min Invites to Withdraw</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {Object.entries(referralSettings.tiers || {}).map(([tierKey, tierVal]) => (
                        <div key={tierKey} className="border border-[#E8E1D5] rounded p-3" data-testid={`tier-view-${tierKey}`}>
                          <div className="text-xs font-bold uppercase text-text-primary capitalize mb-1">{tierKey.replace('_', ' — ')}</div>
                          <div className="text-brand-primary font-heading text-xl font-bold">{tierVal.commission_percentage}%</div>
                          <div className="text-[10px] text-text-secondary">
                            {(tierVal.required_invites > 0 && tierVal.period_days > 0)
                              ? `${tierVal.required_invites} invites / ${tierVal.period_days}d streak`
                              : 'No streak — always earning'}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Platform Commission view */}
                    <div className="pt-3 border-t border-[#E8E1D5]">
                      <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-2">Platform Commission (Instant Pricing markup)</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {['professional', 'vendor', 'venue'].map((grp) => (
                          <div key={grp} className="border border-[#E8E1D5] rounded p-3" data-testid={`pc-view-${grp}`}>
                            <div className="text-xs font-bold uppercase text-text-primary capitalize mb-1">{grp}</div>
                            <div className="text-amber-600 font-heading text-xl font-bold">+{referralSettings.platform_commission?.[grp] ?? 0}%</div>
                            <div className="text-[10px] text-text-secondary">added on listing price</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Advance / Confirmation Fee + Refund Policy */}
            <Card className="bg-white border-[#E8E1D5] mb-6" data-testid="pro-fees-card">
              <CardHeader>
                <CardTitle className="font-heading text-2xl flex items-center gap-2"><Wallet size={24} className="text-brand-primary" /> Advance Payment & Refund Policy</CardTitle>
                <CardDescription>
                  Per-head advance the customer pays to lock in their professionals (full settlement). Platform commission is added on top. Refund schedule kicks in on cancellation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Pro Advance Per Head (₹)</Label>
                    <Input type="number" min={0} max={100000} value={proFees.pro_confirmation_fee_inr}
                      onChange={(e) => setProFees({ ...proFees, pro_confirmation_fee_inr: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9" data-testid="pro-fee-input" />
                    <p className="text-[10px] text-text-secondary">Priest, Chef, Makeup, Mehendi, Choreographer, etc. Default ₹1,000.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Server/Waiter Advance Per Head (₹)</Label>
                    <Input type="number" min={0} max={100000} value={proFees.waiter_confirmation_fee_inr}
                      onChange={(e) => setProFees({ ...proFees, waiter_confirmation_fee_inr: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9" data-testid="waiter-fee-input" />
                    <p className="text-[10px] text-text-secondary">Default ₹200.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Refund Window (days)</Label>
                    <Input type="number" min={0} max={90} value={proFees.refund_window_days}
                      onChange={(e) => setProFees({ ...proFees, refund_window_days: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9" data-testid="refund-window-input" />
                    <p className="text-[10px] text-text-secondary">Default 3.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Early Refund (%)</Label>
                    <Input type="number" min={0} max={100} value={proFees.refund_early_pct}
                      onChange={(e) => setProFees({ ...proFees, refund_early_pct: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9" data-testid="refund-early-input" />
                    <p className="text-[10px] text-text-secondary">≥ window days. Default 50.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Late Refund (%)</Label>
                    <Input type="number" min={0} max={100} value={proFees.refund_late_pct}
                      onChange={(e) => setProFees({ ...proFees, refund_late_pct: e.target.value })}
                      className="rounded-none border-[#E8E1D5] h-9" data-testid="refund-late-input" />
                    <p className="text-[10px] text-text-secondary">&lt; window days. Default 25.</p>
                  </div>
                </div>
                <div className="flex justify-end pt-1">
                  <Button onClick={handleSaveProFees} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-pro-fees-btn">
                    Save Advance & Refund
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Withdrawal Requests */}
            <Card className="bg-white border-[#E8E1D5]">
              <CardHeader>
                <CardTitle className="font-heading text-2xl flex items-center gap-2"><Wallet size={24} className="text-brand-primary" /> Withdrawal Requests</CardTitle>
                <CardDescription>Approve or reject professional withdrawal requests</CardDescription>
              </CardHeader>
              <CardContent>
                {withdrawals.length === 0 ? (
                  <p className="text-center text-text-secondary py-4">No withdrawal requests</p>
                ) : (
                  <div className="space-y-3">
                    {withdrawals.map((w, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-4 border rounded ${
                        w.status === 'pending' ? 'border-amber-300 bg-amber-50/30' :
                        w.status === 'approved' ? 'border-green-300 bg-green-50/30' :
                        'border-red-300 bg-red-50/30'
                      }`} data-testid={`withdrawal-${w.user_id}`}>
                        <div className="flex items-center gap-3">
                          {w.status === 'approved' ? <CheckCircle size={24} className="text-green-600" /> :
                           w.status === 'rejected' ? <XCircle size={24} className="text-red-500" /> :
                           <HourglassSimple size={24} className="text-amber-500" />}
                          <div>
                            <span className="font-heading text-xl font-bold">₹{Number(w.amount).toLocaleString('en-IN')}</span>
                            <span className="text-sm text-text-secondary ml-2">{w.user_name}</span>
                            <span className="text-xs text-text-secondary block">{new Date(w.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${
                            w.status === 'approved' ? 'bg-green-100 text-green-700' :
                            w.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{w.status}</span>
                          {w.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button onClick={() => handleApproveWithdrawal(w.user_id)} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-none text-xs" data-testid={`approve-withdrawal-${w.user_id}`}>
                                Approve
                              </Button>
                              <Button onClick={() => handleRejectWithdrawal(w.user_id)} size="sm" variant="outline" className="rounded-none border-red-300 text-red-500 text-xs" data-testid={`reject-withdrawal-${w.user_id}`}>
                                Reject
                              </Button>
                            </div>
                          )}
                          {w.processed_at && <span className="text-xs text-text-secondary">{new Date(w.processed_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="kyc">
            <div className="space-y-6">
              <AdminKycFormBuilderCard />
              <AdminJoiningFeePanel />
              <AdminProResponseWindow />
              <AdminVendorPaymentModePanel />
              <AdminKycPanel />
            </div>
          </TabsContent>

          <TabsContent value="templates">
            <AdminTemplatesCard />
          </TabsContent>

          <TabsContent value="segments">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-2xl">Instant Pricing Segments</CardTitle>
                    <CardDescription>Toggle which segments customers see for each event type. Disabled segments are completely hidden.</CardDescription>
                  </div>
                  <Button onClick={saveSegments} disabled={!segmentsConfig || savingSegments}
                    className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                    data-testid="save-segments-btn">
                    {savingSegments ? 'Saving…' : 'Save All'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!segmentsConfig ? (
                  <p className="text-sm text-text-secondary">Loading…</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="segments-matrix">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-sand-100 border-b border-[#E8E1D5]">
                          <th className="sticky left-0 bg-sand-100 text-left px-3 py-2 font-bold text-xs uppercase tracking-wider text-text-secondary border-r border-[#E8E1D5]">Event Type</th>
                          {(segmentsConfig.segment_keys || []).map(seg => (
                            <th key={seg} className="px-1 py-2 text-center font-bold text-[10px] uppercase tracking-wider text-text-secondary whitespace-nowrap align-bottom h-28" title={SEGMENT_LABELS[seg] || seg}>
                              <div className="origin-bottom-left -rotate-45 translate-y-2 w-6">
                                <span className="inline-block">{SEGMENT_LABELS[seg] || seg}</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(segmentsConfig.event_types || []).map(ev => (
                          <tr key={ev} className="border-b border-[#E8E1D5] hover:bg-sand-50">
                            <td className="sticky left-0 bg-white text-left px-3 py-2 font-medium text-xs whitespace-nowrap border-r border-[#E8E1D5]" data-testid={`segment-row-${ev}`}>
                              {EVENT_LABELS[ev] || ev}
                            </td>
                            {(segmentsConfig.segment_keys || []).map(seg => {
                              const enabled = segmentsConfig.segments?.[ev]?.[seg] !== false;
                              return (
                                <td key={seg} className="px-2 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => toggleSegment(ev, seg)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                    data-testid={`segment-toggle-${ev}-${seg}`}
                                    aria-label={`${enabled ? 'Disable' : 'Enable'} ${SEGMENT_LABELS[seg]} for ${EVENT_LABELS[ev]}`}
                                  >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-text-secondary mt-3">
                      Tip: Hover a column header to see the full segment name. Decoration sub-categories (Indoor / Outdoor / Lighting / Flower / House) only show when the parent Decoration card is rendered (i.e., for non-birthday events).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deletion Requests Tab */}
          <TabsContent value="deletion">
            <Card className="bg-white border-[#E8E1D5]" data-testid="admin-deletion-card">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Account Deletion Requests</CardTitle>
                <CardDescription>
                  Public deletion requests submitted via <code>/delete-my-account</code>. Approve to cascade-delete the user's data, or reject if the phone is not associated with a real account.
                  Required by Google Play Store policy + DPDP Act 2023.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(!deletionRequests || deletionRequests.length === 0) ? (
                  <p className="text-center text-text-secondary py-6">No pending deletion requests.</p>
                ) : (
                  <div className="space-y-3">
                    {deletionRequests.map((dr) => (
                      <div key={dr.id} className={`p-4 border rounded ${
                        dr.status === 'pending' ? 'border-amber-300 bg-amber-50/30' :
                        dr.status === 'processed' ? 'border-green-300 bg-green-50/30' :
                        'border-gray-300 bg-gray-50/30'
                      }`} data-testid={`deletion-request-${dr.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="text-sm flex-1 min-w-0">
                            <div className="font-medium text-text-primary">
                              {dr.user_name || '(no account found)'} <span className="text-text-secondary font-normal">• {dr.phone}</span>
                            </div>
                            {dr.email && <div className="text-xs text-text-secondary">{dr.email}</div>}
                            {dr.reason && <div className="text-xs italic text-text-secondary mt-1">"{dr.reason}"</div>}
                            <div className="text-[10px] text-text-secondary mt-1">
                              Submitted: {new Date(dr.created_at).toLocaleString()} • Account: {dr.user_found ? `${dr.user_role}` : 'NOT FOUND'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${
                              dr.status === 'pending' ? 'bg-amber-200 text-amber-900' :
                              dr.status === 'processed' ? 'bg-green-200 text-green-900' :
                              'bg-gray-200 text-gray-700'
                            }`}>
                              {dr.status}
                            </span>
                            {dr.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleProcessDeletion(dr.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white rounded-none text-xs"
                                  data-testid={`process-deletion-${dr.id}`}
                                >
                                  Process Delete
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectDeletion(dr.id)}
                                  className="rounded-none border-[#E8E1D5] text-xs"
                                  data-testid={`reject-deletion-${dr.id}`}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports / Moderation Tab */}
          <TabsContent value="reports">
            <Card className="bg-white border-[#E8E1D5]" data-testid="admin-reports-card">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">UGC Reports & Moderation</CardTitle>
                <CardDescription>
                  User-submitted reports of vendor listings, photos, themes and packages.
                  Required by Google Play UGC policy. Pending reports show first.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(!reports || reports.length === 0) ? (
                  <p className="text-center text-text-secondary py-6">No reports yet. The community is well-behaved 🎉</p>
                ) : (
                  <div className="space-y-3">
                    {[...reports].sort((a, b) => (a.status === 'pending' ? -1 : 1)).map((r) => (
                      <div
                        key={r.id}
                        className={`p-4 border rounded ${
                          r.status === 'pending' ? 'border-red-300 bg-red-50/30' :
                          r.status === 'actioned' ? 'border-green-300 bg-green-50/30' :
                          'border-gray-300 bg-gray-50/30'
                        }`}
                        data-testid={`report-${r.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="text-sm flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-medium text-text-primary">{r.target_name || r.target_id}</span>
                              <span className="text-[10px] tracking-wider uppercase font-bold bg-text-primary/5 px-2 py-0.5 rounded">{r.target_type}</span>
                              <span className="text-[10px] tracking-wider uppercase font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">{r.category}</span>
                            </div>
                            {r.description && <div className="text-xs italic text-text-secondary mt-1">"{r.description}"</div>}
                            <div className="text-[10px] text-text-secondary mt-1">
                              Reported by {r.reporter_name || r.reporter_role} · {new Date(r.created_at).toLocaleString()}
                              {r.action_taken && <span> · Action: <strong>{r.action_taken}</strong></span>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${
                              r.status === 'pending' ? 'bg-red-200 text-red-900' :
                              r.status === 'actioned' ? 'bg-green-200 text-green-900' :
                              'bg-gray-200 text-gray-700'
                            }`}>
                              {r.status}
                            </span>
                            {r.status === 'pending' && (
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" onClick={() => handleReportAction(r.id, 'dismiss')} variant="outline" className="rounded-none border-[#E8E1D5] text-xs" data-testid={`report-dismiss-${r.id}`}>Dismiss</Button>
                                <Button size="sm" onClick={() => handleReportAction(r.id, 'hide')} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`report-hide-${r.id}`}>Hide</Button>
                                {r.target_type === 'vendor' && (
                                  <Button size="sm" onClick={() => handleReportAction(r.id, 'ban')} className="rounded-none bg-red-600 hover:bg-red-700 text-white text-xs" data-testid={`report-ban-${r.id}`}>Ban</Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Settings Tab */}
          <TabsContent value="site">
            <Card className="bg-white border-[#E8E1D5]" data-testid="admin-site-card">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Site Settings — Contact Info</CardTitle>
                <CardDescription>
                  These values appear in the public Footer, Privacy Policy (Grievance Officer), Terms (Contact), Refund Policy, and the Delete-Account page.
                  All fields are required and validated. <strong>Make sure emails actually work</strong> — Google Play Store requires reachable contact info.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!siteSettings ? (
                  <p className="text-sm text-text-secondary">Loading...</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'company_name', label: 'Company / Brand Name', placeholder: 'BeeWeddy' },
                      { key: 'owner_name', label: 'Owner Name', placeholder: 'Vignesh', help: 'Shown in Footer copyright line' },
                      { key: 'support_email', label: 'Support Email', type: 'email', placeholder: 'support@beeweddi.com', help: 'Shown in Footer + Refund Policy' },
                      { key: 'grievance_email', label: 'Grievance Officer Email', type: 'email', placeholder: 'privacy@beeweddi.com', help: 'Required by IT Rules 2021' },
                      { key: 'legal_email', label: 'Legal / Terms Email', type: 'email', placeholder: 'legal@beeweddi.com' },
                      { key: 'phone', label: 'Phone', type: 'tel', placeholder: '+91 95000 00000' },
                      { key: 'whatsapp', label: 'WhatsApp', type: 'tel', placeholder: '+91 95000 00000' },
                      { key: 'address', label: 'Address', placeholder: 'Tamil Nadu, India' },
                      { key: 'jurisdiction_city', label: 'Jurisdiction City', placeholder: 'Coimbatore', help: 'Used in Terms §12' },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary block mb-1">{f.label}</label>
                        <input
                          type={f.type || 'text'}
                          value={siteSettings[f.key] || ''}
                          onChange={(e) => updateSiteField(f.key, e.target.value)}
                          placeholder={f.placeholder}
                          className="w-full px-3 py-2 border border-[#E8E1D5] rounded-none text-sm focus:outline-none focus:border-brand-primary"
                          data-testid={`site-${f.key.replace(/_/g, '-')}`}
                        />
                        {f.help && <p className="text-[10px] text-text-secondary mt-1">{f.help}</p>}
                      </div>
                    ))}
                    <div className="md:col-span-2 flex items-center gap-3 pt-2">
                      <Button
                        onClick={handleSaveSiteSettings}
                        disabled={savingSiteSettings}
                        className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                        data-testid="save-site-settings-btn"
                      >
                        {savingSiteSettings ? 'Saving...' : 'Save Site Settings'}
                      </Button>
                      <span className="text-xs text-text-secondary">Changes apply across the app immediately.</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HEATMAPS TAB — admin analytics. All heatmaps live here so they
              stay easy to scan side-by-side. New heatmaps (bookings, revenue,
              calendar density) get added as additional cards under this tab. */}
          <TabsContent value="heatmaps">
            <div className="space-y-6">
              {/* City Demand Heatmap — undersupplied cities surface at the top
                  (demand from search activity vs registered vendors). */}
              <AdminCityDemandHeatmap />

              {/* Event × City — grounded-search query counts in a 2-D grid. */}
              <AdminEventCityHeatmap />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
